import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SouthTynesideCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="South Tyneside Pothole Claims | 9,594 Repairs in One Year | Fixtyer"
        description="South Tyneside Council filled 9,594 potholes in 2023/24 and reports 19% of its 448km estate road network in RED condition — up from 12% in 2020/21. See what the DfT AMBER rating means for your claim."
        canonical="/councils/south-tyneside"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'South Tyneside', url: '/councils/south-tyneside' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'South Tyneside has a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because South Tyneside projects £5.71m capital spend against a £3.71m DfT allocation in 2025/26 and targets 79.9% preventative maintenance. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. The overall AMBER rating reflects estate-road condition: 19% of unclassified roads in RED on the council\'s own 2024/25 data, with only one-third of that network surveyed in any given year.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'Unclassified roads make up 447.6km — 79.7% of South Tyneside\'s 561.8km carriageway network. The council\'s transparency report states this network is "generally in a poorer condition" than classified A, B and C roads. At the latest survey, 19% of U-roads were in RED condition — roughly 85km of estate streets, back lanes and village routes in South Shields, Jarrow and Hebburn. That is where most pothole claims happen.',
          },
          {
            question: 'Does the 22% U-road RED figure in 2022/23 weaken my claim?',
            answer: 'Treat it carefully. South Tyneside\'s report notes 2022/23 unclassified figures were "currently being audited and subject to a resurvey." The comparable trend still shows RED condition rising from 12% in 2020/21 to 20% in 2023/24 and 19% in 2024/25. Whether your incident falls in the audited year or not, the council acknowledges a sustained estate-road backlog — not a one-year blip.',
          },
          {
            question: 'The council only surveys one-third of U-roads each year — does that help my claim?',
            answer: 'It can. South Tyneside surveys approximately 33% of its unclassified network annually by coarse visual inspection, targeting full coverage over three years. That means two-thirds of estate roads go uncondition-surveyed in any given year. For Section 58, ask when your road was last surveyed, whether the defect was visible between surveys, and whether safety inspections — separate from condition surveys — should have caught it sooner.',
          },
          {
            question: 'South Tyneside filled 9,594 potholes in 2023/24 — does that mean the roads are fixed?',
            answer: 'No. The council still recorded 6,380 pothole repairs in 2024/25 and 32,636 over five years — yet U-road RED condition remains at 19%. High reactive volume on a network where estate roads are deteriorating is evidence that defects form faster than structural maintenance catches up, not proof your specific pothole was reasonably managed.',
          },
          {
            question: 'Does the Network North admission about insurance claims help my case?',
            answer: 'It can strengthen context. South Tyneside\'s own Network North publication states additional resurfacing on Perth Avenue and Hedworth Lane would repair roads that "have deteriorated faster than expected and have received numerous insurance claims and complaints." That is documented council knowledge that certain routes were producing claims before targeted works — relevant when arguing a defect should have been prioritised.',
          },
        ]}
      />
      <ServiceSchema
        councilName="South Tyneside Council"
        councilSlug="south-tyneside"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/south-tyneside"
        headline="South Tyneside Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">South Tyneside</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            South Tyneside: 9,594 Pothole Repairs in One Wet Winter
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            South Tyneside earns <strong className="text-emerald-400">GREEN</strong> on the DfT spend scorecard and
            targets nearly 80% preventative capital work in 2025/26. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because the council filled{' '}
            <strong className="text-rose-400">9,594 potholes in 2023/24 alone</strong>,{' '}
            <strong className="text-rose-400">19% of its 448km estate road network</strong> remains in RED condition,
            and only one-third of those residential routes are condition-surveyed in any given year.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">9,594</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2023/24</div>
            <div className="text-brand-300 text-sm mt-1">
              A 66% jump on the previous year&apos;s 5,766 repairs — while U-road RED condition sat at 20% and the
              council resurfaced just 5.5km of carriageway across the whole borough.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network size */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A 562km Network Built on Estate Roads
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from South Tyneside&apos;s own June 2025 transparency report — where claims actually happen
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">RED condition (2024/25)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">A-roads</td>
                  <td className="px-6 py-4 text-center font-mono">48.5</td>
                  <td className="px-6 py-4 text-center">8.6%</td>
                  <td className="px-6 py-4 text-center text-emerald-700 font-medium">1.8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">65.7</td>
                  <td className="px-6 py-4 text-center">11.7%</td>
                  <td className="px-6 py-4 text-center text-emerald-700 font-medium">3%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-semibold text-amber-900">Unclassified roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">447.6</td>
                  <td className="px-6 py-4 text-center font-bold">79.7%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">19%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageway</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">561.8</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <p className="text-amber-800 font-medium">
              At 19% RED on 447.6km of U-roads, roughly <strong>85km of estate streets</strong> should be considered
              for maintenance — against just 5.5km of carriageway resurfaced in 2024/25. The council&apos;s own report
              states the unclassified network is &ldquo;much larger than our classified A, B and C road network and is
              generally in a poorer condition.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and coarse visual inspection data from South Tyneside&apos;s transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (48.5km): stable and well-maintained</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">1.8%</div>
                <div className="text-sm text-rose-800">RED (2024/25)</div>
                <div className="text-xs text-surface-500">up from 1.1% in 2020/21</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">16.4%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">81.8%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down slightly from 83.3%</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Main roads are surveyed annually at 100% coverage by SCANNER. Condition is broadly stable — but A-roads
              are less than 9% of the network.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <h3 className="font-semibold text-brand-900 px-6 pt-6 pb-2">B and C roads (65.7km): flat RED, improving green</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-y border-surface-200">
                  <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                  <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                  <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2020/21</td>
                  <td className="px-4 py-2 text-center">3%</td>
                  <td className="px-4 py-2 text-center">21%</td>
                  <td className="px-4 py-2 text-center">76%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2021/22</td>
                  <td className="px-4 py-2 text-center">4%</td>
                  <td className="px-4 py-2 text-center">22%</td>
                  <td className="px-4 py-2 text-center">74%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2022/23</td>
                  <td className="px-4 py-2 text-center">3%</td>
                  <td className="px-4 py-2 text-center">20%</td>
                  <td className="px-4 py-2 text-center">77%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-2 font-mono">2023/24</td>
                  <td className="px-4 py-2 text-center">3%</td>
                  <td className="px-4 py-2 text-center">20%</td>
                  <td className="px-4 py-2 text-center">77%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                  <td className="px-4 py-2 text-center font-bold">3%</td>
                  <td className="px-4 py-2 text-center font-bold">19%</td>
                  <td className="px-4 py-2 text-center font-bold text-emerald-700">78%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (447.6km): the AMBER story</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">12%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">16%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">
                      22%* <span className="text-surface-400 text-xs font-normal">(subject to audit/resurvey)</span>
                    </td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">20%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">19%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED-condition U-roads are up more than half since 2020/21 (12% → 19%). Even at the latest figure, that
              is roughly 85km of residential network the council&apos;s own surveys say should be considered for
              maintenance.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The three-year survey gap</h3>
            <p className="text-brand-200 mb-4">
              South Tyneside surveys approximately <strong className="text-white">33% of unclassified roads each year</strong>{' '}
              by coarse visual inspection, with a target of covering the whole U-road network over three years. For
              Section 58, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your estate road last condition-surveyed — year one, two or three of the cycle?</li>
              <li>• If 19% of U-roads were RED at the last borough-wide comparable survey, what was done about yours?</li>
              <li>• Did a pothole form and worsen in the two-thirds of the network not surveyed that year?</li>
              <li>• Does a safety inspection regime compensate for gaps in condition surveying on residential routes?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge on roads it only condition-surveys once every three years
              — while admitting they are generally in poorer condition than classified routes.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            32,636 potholes in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume on a network where estate-road RED condition keeps rising
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Daily average</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">4,881</td>
                  <td className="px-6 py-4 text-center">~13</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">6,015</td>
                  <td className="px-6 py-4 text-center">~16</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">5,766</td>
                  <td className="px-6 py-4 text-center">~16</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9,594</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~26</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">6,380</td>
                  <td className="px-6 py-4 text-center">~17</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">32,636</td>
                  <td className="px-6 py-4 text-center font-bold">~18</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2023/24 spike</h3>
              <p className="text-surface-600 text-sm">
                Pothole repairs jumped 66% from 5,766 to 9,594 in 2023/24 — the same year U-road RED condition hit
                20% and the council&apos;s Network North programme targeted roads that had &ldquo;deteriorated faster
                than expected.&rdquo; The council forecasts approximately 6,500 repairs in 2025/26, but past years show
                reactive demand tracks weather and network condition, not forecasts alone.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Repairs vs resurfacing</h3>
              <p className="text-amber-800 text-sm">
                In 2024/25 the council resurfaced 5.5km of carriageway and carried out 1,560m² of patching — while
                filling 6,380 potholes. The 2025/26 target is 9km of resurfacing. Reactive fill volume and structural
                treatment operate on different scales: thousands of pothole patches do not equal kilometres of renewal
                on an 85km RED backlog.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spend */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN spend on an AMBER-condition network
          </h2>
          <p className="text-surface-600 mb-8">
            South Tyneside invests above its DfT allocation — yet estate roads keep slipping
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">3,742,000</td>
                  <td className="px-4 py-3 text-center">5,507,000</td>
                  <td className="px-4 py-3 text-center">1,127,884</td>
                  <td className="px-4 py-3 text-center">83.0%</td>
                  <td className="px-4 py-3 text-center">17.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">3,177,428</td>
                  <td className="px-4 py-3 text-center">7,133,000</td>
                  <td className="px-4 py-3 text-center">1,147,110</td>
                  <td className="px-4 py-3 text-center">86.1%</td>
                  <td className="px-4 py-3 text-center">13.9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">2,781,000</td>
                  <td className="px-4 py-3 text-center">6,040,000</td>
                  <td className="px-4 py-3 text-center">1,127,040</td>
                  <td className="px-4 py-3 text-center">84.3%</td>
                  <td className="px-4 py-3 text-center">15.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">3,022,000</td>
                  <td className="px-4 py-3 text-center">6,632,000</td>
                  <td className="px-4 py-3 text-center">1,147,490</td>
                  <td className="px-4 py-3 text-center">85.2%</td>
                  <td className="px-4 py-3 text-center">14.8%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">3,022,000</td>
                  <td className="px-4 py-3 text-center">5,290,000</td>
                  <td className="px-4 py-3 text-center">1,021,875</td>
                  <td className="px-4 py-3 text-center">83.8%</td>
                  <td className="px-4 py-3 text-center">16.2%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26*</td>
                  <td className="px-4 py-3 text-center font-bold">3,710,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">5,710,000</td>
                  <td className="px-4 py-3 text-center">1,437,000</td>
                  <td className="px-4 py-3 text-center font-bold">79.9%</td>
                  <td className="px-4 py-3 text-center">20.1%</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-surface-500 px-4 py-2">* 2025/26 figures are estimates per the council report.</p>
          </div>

          <QuoteCard
            quote="The proportion that is spent on reactive maintenance is determined by the condition of the network and what is determined to be 'actionable' and in need of repair. Whilst this is kept to a minimum, the council's statutory duty to keep the highway safe is the overriding factor."
            source="South Tyneside Council Local Highway Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Network North admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The insurance-claims admission
          </h2>
          <p className="text-surface-600 mb-8">
            South Tyneside&apos;s own explanation for targeted resurfacing — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The additional investment will repair roads that have deteriorated faster than expected and have received numerous insurance claims and complaints."
              source="South Tyneside Council — Highway maintenance funding increase (Network North), published 2024"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                South Tyneside publicly acknowledges that Perth Avenue in South Shields and Hedworth Lane in Jarrow
                deteriorated faster than planned and generated &ldquo;numerous insurance claims and complaints&rdquo;
                before Network North resurfacing was brought forward in 2024/25.
              </p>
              <p className="text-surface-600 text-sm">
                Documented claims history on specific corridors raises the bar for what a reasonable inspection and
                repair regime looks like on those routes under Section 58.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road on or near Perth Avenue, Hedworth Lane or River Drive?</li>
                <li>• Were prior insurance claims on the same defect logged before your incident?</li>
                <li>• If the council knew claims were clustering, why was the pothole still there when you hit it?</li>
                <li>• Does your road appear on the published 2025/26 highways programme?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Claims analysis */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a well-funded AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: South Tyneside is not failing on spend — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — £5.71m projected capital vs £3.71m DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 79.9% preventative capital spend forecast for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-roads at 1.8% RED — main routes broadly stable
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Band 3 highway asset management assessment maintained since 2021
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Permit scheme, streetworks coordination and warm-mix innovation cited
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — U-road RED up from 12% to 19% since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 447.6km of estate roads — 80% of the network, generally poorer condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only ~33% of U-roads condition-surveyed each year on a three-year cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 32,636 pothole repairs in five years — including 9,594 in one year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admission of roads with &ldquo;numerous insurance claims&rdquo; before resurfacing
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and Band 3 asset management, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online form) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Estate-road location — the three-year U-road survey gap is your strongest structural argument</li>
              <li>• Whether your road matches Network North corridors with documented claim clusters</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              South Tyneside&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to South Tyneside Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting creates a dated record the council cannot ignore — and prior reports strengthen Section 58
            rebuttals if you later claim for damage.
          </p>
          <a
            href="https://www.southtyneside.gov.uk/article/1483/Report-a-pothole-in-the-road"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole to South Tyneside Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in South Tyneside?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No three-year U-road survey-gap argument</li>
                <li>• No 9,594-repair spike cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 19% U-road RED backlog documented</li>
                <li>✅ Three-year survey cycle argued</li>
                <li>✅ 32,636 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to South Tyneside</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=south-tyneside"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my South Tyneside claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                South Tyneside has a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because South Tyneside projects £5.71m capital spend against a
                £3.71m DfT allocation in 2025/26 and targets 79.9% preventative maintenance. Section 58 turns on whether
                the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate
                spend. The overall AMBER rating reflects estate-road condition: 19% of unclassified roads in RED on the
                council&apos;s own 2024/25 data.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 447.6km — 79.7% of South Tyneside&apos;s 561.8km carriageway network. The
                council&apos;s transparency report states this network is &ldquo;generally in a poorer condition&rdquo;
                than classified routes. At the latest survey, 19% of U-roads were in RED condition — roughly 85km of
                estate streets across South Shields, Jarrow and Hebburn.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 22% U-road RED figure in 2022/23 weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Treat it carefully. South Tyneside&apos;s report notes 2022/23 unclassified figures were &ldquo;currently
                being audited and subject to a resurvey.&rdquo; The comparable trend still shows RED condition rising
                from 12% in 2020/21 to 20% in 2023/24 and 19% in 2024/25 — a sustained estate-road backlog, not a
                one-year anomaly.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council only surveys one-third of U-roads each year — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. South Tyneside surveys approximately 33% of its unclassified network annually by coarse visual
                inspection, targeting full coverage over three years. That means two-thirds of estate roads go
                uncondition-surveyed in any given year — a structural gap in network knowledge that matters for Section
                58 on residential routes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                South Tyneside filled 9,594 potholes in 2023/24 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The council still recorded 6,380 pothole repairs in 2024/25 and 32,636 over five years — yet U-road
                RED condition remains at 19%. High reactive volume on a deteriorating estate network is evidence that
                defects form faster than structural maintenance catches up, not proof your specific pothole was
                reasonably managed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the Network North admission about insurance claims help my case?
              </h3>
              <p className="text-surface-600 text-sm">
                It can strengthen context. South Tyneside&apos;s own Network North publication states additional
                resurfacing would repair roads that &ldquo;have deteriorated faster than expected and have received
                numerous insurance claims and complaints.&rdquo; That is documented council knowledge that certain
                routes were producing claims before targeted works — relevant when arguing a defect should have been
                prioritised.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://southtyneside.gov.uk/article/27257/Local-Highway-Maintenance-Transparency-Report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              South Tyneside Council Local Highway Maintenance Transparency Report (June 2025)
            </a>{' '}
            |{' '}
            <a
              href="https://www.southtyneside.gov.uk/article/22175/Highway-maintenance-funding-increase-Network-North"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              South Tyneside Council — Highway maintenance funding increase (Network North)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
